import { isAppInIFrame } from '@thoughtspot/embed-util';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
    OBSERVABILITY_CONFIG,
} from '@thoughtspot/feature-service';
import { getClientLocale } from '@thoughtspot/i18n';
import {
    Context,
    getLogger,
    getMeter,
    getTracer,
    initReplayService,
    initTelemetry,
    Logger,
    Meter,
    otelContext,
    otelTrace,
    propagation,
    Span,
    SpanKind,
    SpanStatusCode,
    startAnonymizedReplay,
    startFullReplay as startFullSessionReplay,
    stopReplay,
    ValueType,
} from '@thoughtspot/observability';
import {
    isFrontendTracingEnabled,
    isNotAProductionCluster,
} from '@thoughtspot/session-service';
import {
    createContext,
    useContext,
    useEffect,
    useReducer,
    useRef,
} from 'react';
import { FLAGS, flags } from '../config-service/flags-service';
import {
    getClusterId,
    getClusterName,
    getCurrentOrgId,
    getReleaseVersion,
    getSentryAnonymizedDSN,
    getSentryFullDSN,
    getUserGuid,
} from '../config-service/session-service';
import {
    OTLP_CONFIG,
    PROPAGATED_OPERATIONS,
    TraceComponent,
    TRACED_OPERATION,
    TracedOperation,
} from './observability-service.const';
import {
    getRootContext,
    isTelemetryInitialized,
    onTelemetryReady,
    setTelemetryEnabled,
    startRootSpan,
} from './trace-context-manager';

export { TRACE_COMPONENT, TRACED_OPERATION } from './observability-service.const';
export type {
    TraceComponent,
    TracedOperation,
} from './observability-service.const';

const otelDebugLog = (...args: any[]) => {
    if (flags.getValue(FLAGS.debug?.name)) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

// Constants for timeouts
const PENDING_REQUEST_CLEANUP_MS = 30000; // 30 seconds auto-cleanup
const SPAN_END_DELAY_MS = 100; // Delay before ending component spans
// Long enough to outlive a full liveboard render cycle, short enough to avoid
// accumulating stale entries when vizes are added/removed dynamically.
const VIZ_SPAN_CONTEXT_TTL_MS = 10000; // 10 seconds

// ─── Request-Context Mapping ────────────────────────────────────────────────
// Bridges the gap between "a component creates a span" and "an async API call
// happens later" so we can attribute the API call back to the component that
// triggered it.
//
// Flow:
//   1. Component calls registerTraceContext(traceKey, context) before the API call.
//   2. API call is fired; its body contains traceKey somewhere in the GraphQL variables.
//   3. Custom fetch reads the body, looks up the context by traceKey, and uses
//      it as the parent for the request span.
//   4. Entries auto-clean after PENDING_REQUEST_CLEANUP_MS to prevent leaks.
//
// A Map (keyed by traceKey) is used instead of a global stack because async
// operations can interleave — a stack would attribute the wrong API call to
// the wrong component.
const pendingRequestContexts = new Map<
    string,
    {
        context: Context;
        componentName: string;
        timestamp: number;
    }
>();

// ------
// Viz Span Context Registry
// ------
// Maps a viz GUID to the OTel Context holding its ContextBookLoader span.
//
// Problem: ContextBookLoader and VizContainer share the same viz GUID but are
// siblings in the React tree (not parent-child), so OTelTracingContext cannot
// propagate from one to the other. This registry bridges that gap.
//
// Flow:
// 1. ContextBookLoader calls registerVizSpanContext(vizId, context) during render.
// 2. VizContainer calls getVizSpanContext(vizId) during its first render and passes
//    the result as the explicit parent to useTracing, making it a child span.
//
// Timing: React renders JSX siblings in order. In pinboard-tile-layout.tsx,
// all ContextBookLoader components appear before the grid of VizContainers, so
// registration always happens before the lookup in the same synchronous pass.
const vizSpanContextRegistry = new Map<
    string,
    { context: Context; timestamp: number }
>();

/**
 * Registers the OTel context of a ContextBookLoader span so that the
 * corresponding VizContainer can adopt it as its parent span.
 *
 * Must be called synchronously during render (not inside useEffect) so that
 * the entry is present when VizContainer renders in the same React pass.
 *
 * @param vizId - The viz/container GUID shared by ContextBookLoader and VizContainer.
 * @param context - The OTel context returned by useTracing inside ContextBookLoader.
 */
export const registerVizSpanContext = (
    vizId: string,
    context: Context,
): void => {
    if (!isTelemetryInitialized()) return;
    const timestamp = Date.now();
    vizSpanContextRegistry.set(vizId, { context, timestamp });
    setTimeout(() => {
        const entry = vizSpanContextRegistry.get(vizId);
        if (entry?.timestamp === timestamp) {
            vizSpanContextRegistry.delete(vizId);
        }
    }, VIZ_SPAN_CONTEXT_TTL_MS);
};

/**
 * Returns the OTel context registered by the ContextBookLoader for this viz,
 * or null if no entry exists (e.g. telemetry not yet initialized, or the
 * ContextBookLoader rendered after VizContainer).
 *
 * @param vizId - The viz/container GUID.
 */
export const getVizSpanContext = (vizId: string): Context | null =>
    vizSpanContextRegistry.get(vizId)?.context ?? null;

/**
 * Registers a pending request context for async API call attribution.
 * Call this BEFORE making an API call to associate the request with a component span.
 *
 * The custom fetch instrumentation will look up the context using the trace key
 * extracted from GraphQL variables, enabling proper parent-child span relationships
 * even for async operations.
 *
 * @param traceKey - The value that will be in the GraphQL variables (e.g., pinboardId, contextBookId).
 *                   This must match a field in TRACEABLE_VARIABLE_FIELDS.
 * @param context - The OpenTelemetry context from useTracing hook to associate with this request.
 * @param componentName - Attribution label from TRACE_COMPONENT — identifies the calling component.
 * @param operationName - GraphQL operation name from TRACED_OPERATION. When provided,
 *                        creates a unique key `${operationName}_${traceKey}` to prevent conflicts
 *                        when multiple operations use the same entity ID. Recommended for all
 *                        registrations to ensure proper context isolation.
 *
 * @example
 * // Register before firing GetChartWithData for a viz
 * registerTraceContext(
 *   answerSessionId,
 *   otelContext,
 *   TRACE_COMPONENT.ChartVizContainer,
 *   TRACED_OPERATION.GetChartWithData,
 * );
 * fetchChartData(answerSessionId);
 */
export const registerTraceContext = (
    traceKey: string | null,
    context: Context | null,
    componentName: TraceComponent,
    operationName?: TracedOperation,
) => {
    if (!isTelemetryInitialized() || !context) return;

    // When traceKey is null (e.g. getAllCharts has only $orgId), fall back to
    // the operation name alone so keyless ops can still be attributed.
    let registrationKey: string;
    if (traceKey) {
        registrationKey = operationName
            ? `${operationName}_${traceKey}`
            : traceKey;
    } else {
        registrationKey = operationName ?? '';
    }

    if (!registrationKey) return;

    const existing = pendingRequestContexts.get(registrationKey);
    if (existing && existing.context === context) return;

    pendingRequestContexts.set(registrationKey, {
        context,
        componentName,
        timestamp: Date.now(),
    });

    const registrationTimestamp = Date.now();

    // Compare timestamp on cleanup so re-registered entries aren't deleted.
    setTimeout(() => {
        const entry = pendingRequestContexts.get(registrationKey);
        if (entry && entry.timestamp === registrationTimestamp) {
            pendingRequestContexts.delete(registrationKey);
        }
    }, PENDING_REQUEST_CLEANUP_MS);
};

// Synchronously registers a trace context during render, only when `enabled` is
// truthy. Use in render body when a child component or an Apollo hook in the
// same render dispatches the query before any useEffect can run — moving this
// to useEffect would race with React's child-effects-before-parent ordering and
// drop trace correlation on the first render.
export const useSyncTraceContext = (
    enabled: unknown,
    traceKey: string | null,
    context: Context | null | undefined,
    componentName: TraceComponent,
    operationName?: TracedOperation,
): void => {
    if (!enabled || !context) return;
    registerTraceContext(traceKey, context, componentName, operationName);
};

// Looks up the context registered for a request.
//
// Compound keys (`${operationName}_${traceKey}`) are tried first and DELETED
// after a successful read — they're meant to be one-shot per registration.
//
// Simple keys (just `traceKey`) are tried as a fallback and KEPT, because the
// same key may serve multiple operations (e.g. several queries share a
// pinboardId).
const getContextForRequest = (
    traceKey: string | null,
    operationName?: string,
): { context: Context; componentName: string } | null => {
    if (operationName) {
        // Compound key: operation + trace key (when key is present)
        if (traceKey) {
            const compoundKey = `${operationName}_${traceKey}`;
            const entry = pendingRequestContexts.get(compoundKey);
            if (entry) {
                pendingRequestContexts.delete(compoundKey);
                return {
                    context: entry.context,
                    componentName: entry.componentName,
                };
            }
        }

        // Operation-name-only fallback for keyless ops (e.g. getAllCharts)
        const opOnlyEntry = pendingRequestContexts.get(operationName);
        if (opOnlyEntry) {
            return {
                context: opOnlyEntry.context,
                componentName: opOnlyEntry.componentName,
            };
        }
    }

    if (traceKey) {
        const entry = pendingRequestContexts.get(traceKey);
        if (entry) {
            return {
                context: entry.context,
                componentName: entry.componentName,
            };
        }
    }

    return null;
};

// GraphQL variable fields scanned by the custom fetch to find a context
// registered via registerTraceContext. Add new fields here to support new APIs.
//
// Order matters: more specific fields come first. Generic ones like 'id' and
// 'params' are last so they only match when nothing more specific is present.
// Different APIs use different variable names for the same concept (e.g. some
// use 'pinboardId', others nest it as 'params' or 'id').
const TRACEABLE_VARIABLE_FIELDS = [
    'contextBookId',
    'pinboardId',
    'answerId',
    'sessionId',
    'params', // GetPersonalisedViews uses 'params' for pinboardId
    'id', // LoadPinboardSession uses 'id' for pinboardId
];

// Parses a GraphQL request body and returns the operation name plus a
// (key, field) pair that identifies the traceable entity, if one is present.
// Returns just the operationName if no traceable key is found, so the span
// still gets a readable name.
const extractTraceInfoFromBody = (
    body: string | null,
): {
    key?: string;
    field?: string;
    operationName?: string;
} | null => {
    if (!body) return null;
    try {
        const parsed = JSON.parse(body);
        const variables = parsed?.variables;
        const operationName = parsed?.operationName;

        if (!operationName) return null;

        if (variables?.session?.pinboardId) {
            return {
                key: variables.session.pinboardId,
                field: 'session.pinboardId',
                operationName,
            };
        }

        // Named fields (contextBookId, pinboardId, answerId, …) take priority
        // over session.sessionId so that LoadContextBook is keyed by contextBookId,
        // not by the pinboard session that also appears in the request.
        if (variables) {
            const matchedField = TRACEABLE_VARIABLE_FIELDS.find(
                field => variables[field],
            );
            if (matchedField) {
                return {
                    key: variables[matchedField],
                    field: matchedField,
                    operationName,
                };
            }
        }

        // Bach queries (GetChartWithData, GetAnswerDataByQuery, etc.) carry
        // session.sessionId — fall through here after named fields are checked.
        if (variables?.session?.sessionId) {
            return {
                key: variables.session.sessionId,
                field: 'session.sessionId',
                operationName,
            };
        }

        if (variables?.BachSessionId?.sessionId) {
            return {
                key: variables.BachSessionId.sessionId,
                field: 'BachSessionId.sessionId',
                operationName,
            };
        }

        // Return operationName alone so the span gets a readable name
        // even when no traceable key is present.
        return { operationName };
    } catch {
        return null;
    }
};

// Propagates the OpenTelemetry Context through the React tree.
export const OTelTracingContext = createContext<Context | null>(null);

let LOGGER: Logger = (console as any) as Logger;
let intercomMutationObserver: MutationObserver;
let storedReplayId: string | null = null;

const startFullReplay = async (
    onReplayStart: (replayId: string) => void,
    isUnmaskedReplay: boolean,
) => {
    const fullDSN = getSentryFullDSN();
    if (!fullDSN) {
        LOGGER.error(
            'Sentry DSN is empty; skipping full replay initialization',
        );
        return;
    }
    await initReplayService({
        dsn: fullDSN,
        userId: getUserGuid(),
        releaseVersion: getReleaseVersion(),
        clusterId: getClusterId(),
        clusterName: getClusterName(),
        userLocale: getClientLocale(),
        orgId: getCurrentOrgId().toString(),
        isEmbedded: isAppInIFrame(),
        env: isNotAProductionCluster() ? 'dev' : 'production',
    });
    const replayID = await startFullSessionReplay(isUnmaskedReplay);
    storedReplayId = replayID;
    onReplayStart(replayID);
};

const isUnmaskedReplay = (): boolean => {
    const observabilityConfig: OBSERVABILITY_CONFIG = isFeatureEnabled(
        FEATURE_FLAG_NAMES.OBSERVABILITY_CONFIG,
    );
    return (
        observabilityConfig?.replayUnmaskedTenants?.includes(
            getCurrentOrgId().toString(),
        ) ?? false
    );
};

const watchMessengerFrame = (
    onReplayStart: (replayId: string) => void,
    onReplayStop: (
        replayId: string,
        sendMessage: (message: string) => void,
    ) => void,
) => {
    const intercomMessengerFrame = document.querySelector(
        "[name='intercom-messenger-frame']",
    ) as HTMLIFrameElement;
    if (!intercomMessengerFrame) {
        LOGGER.warn('Intercom messenger frame not found, retrying');
        return;
    }
    const frameBody = intercomMessengerFrame.contentDocument.body;
    intercomMutationObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (
                mutation.type === 'childList' &&
                mutation.addedNodes.length > 0 &&
                mutation.addedNodes[0].nodeType === Node.ELEMENT_NODE &&
                (mutation.addedNodes[0] as Element).querySelectorAll(
                    '[data-testid="conversation-part"]',
                )?.length &&
                mutation.addedNodes[0].textContent?.match(/⏺/g)?.length === 1 &&
                !mutation.addedNodes[0].textContent?.match(/■/g)
            ) {
                startFullReplay(onReplayStart, isUnmaskedReplay());
            }
        });

        const sendButton = frameBody.querySelector(
            "button[aria-label='Send a message…']",
        ) as HTMLButtonElement;
        if (storedReplayId && sendButton) {
            const replayId = storedReplayId;
            storedReplayId = null;
            stopReplay();

            const sendMessage = (message: string) => {
                const inputEl = frameBody.querySelector(
                    'textarea',
                ) as HTMLTextAreaElement;
                if (!inputEl) {
                    LOGGER.warn(
                        'Intercom textarea not found; message not auto-sent',
                    );
                    return;
                }
                inputEl.focus();
                const iframeWindow =
                    intercomMessengerFrame.contentWindow as any;
                const nativeValueSetter = Object.getOwnPropertyDescriptor(
                    iframeWindow.HTMLTextAreaElement.prototype,
                    'value',
                )?.set;
                nativeValueSetter?.call(inputEl, message);
                inputEl.dispatchEvent(
                    new iframeWindow.InputEvent('input', {
                        bubbles: true,
                        inputType: 'insertText',
                        data: message,
                    }),
                );
                setTimeout(() => {
                    if (sendButton.getAttribute('aria-disabled') !== 'true') {
                        sendButton.click();
                    } else {
                        LOGGER.warn(
                            'Send button still disabled after input; message not auto-sent',
                        );
                    }
                }, 100);
            };

            onReplayStop(replayId, sendMessage);
        }
    });
    intercomMutationObserver.observe(frameBody, {
        childList: true,
        subtree: true,
    });
};

export const watchForFullReplay = (
    onReplayStart: (replayId: string) => void,
    onReplayStop: (
        replayId: string,
        sendMessage: (message: string) => void,
    ) => void,
) => {
    if (!(window as any).Intercom) {
        LOGGER.warn('Intercom not found');
    }
    let observer: MutationObserver;
    // TODO (@Sreeni): Swap this out for a prism API poll

    (window as any).Intercom('onShow', () => {
        setTimeout(() => {
            watchMessengerFrame(onReplayStart, onReplayStop);
        }, 1000);
    });
    (window as any).Intercom('onHide', () => {
        observer?.disconnect();
    });
};

export const initializeReplay = async (
    sampleRate: number,
    isUnmaskedReplay: boolean,
) => {
    try {
        const anonymizedDSN = getSentryAnonymizedDSN();
        if (!anonymizedDSN) {
            LOGGER.warn('Sentry DSN is empty; skipping replay initialization');
            return;
        }
        await initReplayService({
            dsn: anonymizedDSN,
            userId: getUserGuid(),
            releaseVersion: getReleaseVersion(),
            clusterId: getClusterId(),
            clusterName: getClusterName(),
            userLocale: getClientLocale(),
            orgId: getCurrentOrgId().toString(),
            isEmbedded: isAppInIFrame(),
            env: isNotAProductionCluster() ? 'dev' : 'production',
        });
        if (Math.random() < sampleRate) {
            LOGGER.info('Sampling replay');
            await startAnonymizedReplay(isUnmaskedReplay);
        }
    } catch (err) {
        LOGGER.error(err);
    }
};

let globalMeter: Meter = null;

type Records = {
    renderCounter?: any;
    firstRenderTimer?: any;
    renderTimer?: any;
};

// Records render-performance metrics for a component:
//   - {componentName}_first_render_time (gauge, ms)
//   - {componentName}_render_count (counter)
//   - {componentName}_render_time (histogram, subsequent renders, ms)
//
// Silently no-ops until telemetry is initialised.
export const useMetrics = (componentName: string) => {
    const records = useRef<Records>({});
    const time = useRef<DOMHighResTimeStamp>(performance.now());
    const isFirstRender = useRef<boolean>(true);

    useEffect(() => {
        if (!globalMeter) {
            try {
                const newMeter = getMeter(componentName);
                globalMeter = newMeter;
            } catch (e) {
                // Telemetry not initialised; fall through to mark firstRender.
            }
        }
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (!globalMeter) {
                return;
            }
            const elapsedTime = performance.now() - time.current;
            // First-renders are heavier than re-renders so count them separately.
            records.current.firstRenderTimer = globalMeter.createGauge(
                `${componentName}_first_render_time`,
            );
            records.current.firstRenderTimer.record(elapsedTime);
            records.current.renderCounter = globalMeter.createCounter(
                `${componentName}_render_count`,
                { valueType: ValueType.INT },
            );
            records.current.renderCounter.add(1);
            time.current = performance.now();
            return;
        }
        if (!globalMeter) {
            return;
        }
        if (!records.current.renderTimer) {
            records.current.renderTimer = globalMeter.createHistogram(
                `${componentName}_render_time`,
            );
        }
        records.current.renderTimer.record(performance.now() - time.current);
        // TODO (Sreeni): Is it worth counting renders with a useState for the else to this if?
        if (records.current.renderCounter) {
            records.current.renderCounter.add(1);
        }
        time.current = performance.now();
    });
};

/*
 * USAGE EXAMPLE:
 *
 * To attribute API calls to a component, wrap the API-calling code with withComponentContext:
 *
 * const MyComponent = () => {
 *     const componentContext = useTracing('MyComponent');
 *
 *     useEffect(() => {
 *         // Wrap API calls to attribute them to this component
 *         withComponentContext(componentContext, () => {
 *             fetch('/api/data'); // This will be a child of MyComponent_render
 *         });
 *     }, []);
 *
 *     // Without wrapping, API calls will have page_load as parent (root)
 *     useEffect(() => {
 *         fetch('/api/background'); // This will be a child of page_load (root)
 *     }, []);
 *
 *     return <div>My Component</div>;
 * };
 */

export const useTracing = (
    componentName: string,
    explicitParentContext?: Context | null,
    additionalAttributes?: Record<string, string | number | boolean>,
    disabled?: boolean,
): Context | null => {
    const spanRef = useRef<any>(null);
    const contextRef = useRef<Context | null>(null);
    const spanEndTimerRef = useRef<any>(null);
    const [, forceUpdate] = useReducer(s => s + 1, 0);

    // useContext must be called unconditionally (hooks rules), but
    // explicitParentContext takes priority when provided.
    const parentOtelContext = useContext(OTelTracingContext);
    const parentContext: Context | null =
        explicitParentContext ??
        parentOtelContext ??
        getRootContext() ??
        otelContext?.active() ??
        null;

    // If telemetry initialises after first render, force a re-render so the
    // span is created.
    useEffect(() => {
        if (!isTelemetryInitialized()) {
            return onTelemetryReady(forceUpdate);
        }
        return undefined;
    }, []);

    // Unmount-cleanup must use [] deps — a [componentName]-dependent effect
    // only runs on mount and would miss spans created on a later forceUpdate
    // re-render, leaving them un-ended in Honeycomb.
    useEffect(() => {
        return () => {
            if (spanEndTimerRef.current) {
                clearTimeout(spanEndTimerRef.current);
            }
            if (spanRef.current?.isRecording()) {
                spanRef.current.end();
            }
        };
    }, []);

    // When disabled, pass through the parent context without creating a span.
    if (disabled) {
        return parentContext;
    }

    if (!spanRef?.current) {
        // We can't create a span here (telemetry not initialised, or
        // getTracer() threw error). parentContext is the OTel context this
        // component inherited from above — it points to the span of the
        // nearest ancestor that did create a span, or to the page_load
        // root span if no ancestor created one.
        //
        // We return parentContext unchanged so that any child component,
        // when it calls useTracing and reads OTelTracingContext, finds the
        // same ancestor span as its parent. Without this, children would
        // see no parent and fall back to page_load, flattening the trace.
        //
        // Example: in tree A - B - C where only A and C create spans,
        // C's span becomes a child of A's span.
        if (!isTelemetryInitialized()) {
            return parentContext;
        }

        let tracer;
        try {
            tracer = getTracer(componentName);
        } catch (error) {
            return parentContext;
        }

        const spanName = `${componentName}_render`;
        const span = tracer.startSpan(
            spanName,
            {
                kind: SpanKind.INTERNAL,
                attributes: {
                    component: componentName,
                    'component.type': 'react',
                    'component.lifecycle': 'render',
                    ...additionalAttributes,
                },
            },
            parentContext,
        );

        spanRef.current = span;

        const spanContext = otelTrace.setSpan(parentContext, span);
        contextRef.current = spanContext;

        // Schedule end here, not in useEffect, so the timer registers whether
        // the span was created on initial mount or a later forceUpdate.
        spanEndTimerRef.current = setTimeout(() => {
            if (spanRef.current?.isRecording()) {
                spanRef.current.end();
            }
        }, SPAN_END_DELAY_MS);
    }

    return contextRef.current || parentContext;
};

// Runs `callback` with the given component context active, so any spans
// created inside (including auto-instrumented fetches) are children of it.
//
// Use this for synchronous work that you want attributed to a component.
// For async API calls, prefer registerTraceContext() — it's more reliable
// because OTel's active context doesn't survive most async boundaries.
//
// Example:
//   withComponentContext(componentContext, () => {
//     fetch('/api/data');  // child of MyComponent_render
//   });
export const withComponentContext = <T,>(
    componentContext: Context,
    callback: () => T,
): T => {
    if (!isTelemetryInitialized()) {
        return callback();
    }
    return otelContext.with(componentContext, callback);
};

// Monkey-patches window.fetch to wrap every outbound request in a span.
//
// For each request:
//   1. Skip OTLP exporter calls and explicitly ignored operations/URLs.
//   2. Try to find a parent context — first via registerTraceContext lookup,
//      then any active OTel context, finally the page_load root.
//   3. Create a CLIENT-kind span as a child of that parent.
//   4. For PROPAGATED_OPERATIONS, inject B3 trace headers so the prism
//      backend can join the same trace.
//   5. Run the actual fetch within the span context, then end the span when
//      the response (or error) arrives.
const installCustomFetchInstrumentation = () => {
    const originalFetch = window.fetch;

    window.fetch = function(
        input: RequestInfo | URL,
        init?: RequestInit,
    ): Promise<Response> {
        let url: string;
        if (typeof input === 'string') {
            url = input;
        } else if (input instanceof URL) {
            url = input.href;
        } else {
            url = (input as Request).url;
        }

        if (!isTelemetryInitialized()) {
            return originalFetch.call(window, input, init);
        }

        // Skip OTLP endpoints to avoid infinite loops.
        if (
            url.includes('/v1/traces') ||
            url.includes('/v1/metrics') ||
            url.includes('/v1/logs')
        ) {
            return originalFetch.call(window, input, init);
        }

        const method = init?.method || 'GET';
        const body = init?.body as string | null;

        const traceInfo = extractTraceInfoFromBody(body);

        // Gate CLIENT span creation; B3 propagation handled below.
        if (
            !traceInfo?.operationName ||
            !(traceInfo.operationName in TRACED_OPERATION)
        ) {
            return originalFetch.call(window, input, init);
        }
        // Instrumentation must never break the host fetch. Wrap the entire
        // span-creation + header-injection block: any synchronous throw here
        // falls through to the original (untraced) fetch so the network
        // request still fires.
        let span: Span | undefined;
        let tracedInit = init;
        try {
            const requestContext = getContextForRequest(
                traceInfo.key ?? null,
                traceInfo.operationName,
            );

            // Prefer request-specific context, then any active span (component
            // context set via withComponentContext), only falling back to the
            // root page_load span when there is no active span.
            const activeCtx = otelContext.active();
            const hasActiveSpan = !!otelTrace.getSpan(activeCtx);
            const parentContext =
                requestContext?.context ||
                (hasActiveSpan ? activeCtx : getRootContext());
            const parentComponent =
                requestContext?.componentName || 'AppView_render';

            const tracer = getTracer('custom-fetch-instrumentation');
            span = tracer.startSpan(
                `graphql ${traceInfo.operationName}`,
                {
                    kind: SpanKind.CLIENT,
                    attributes: {
                        'http.method': method,
                        'http.url': url,
                        'component.trigger': parentComponent,
                        ...(traceInfo.key && {
                            'trace.key': traceInfo.key,
                            'trace.field': traceInfo.field,
                        }),
                    },
                },
                parentContext,
            );

            const spanContext = otelTrace.setSpan(parentContext, span);

            // B3 propagation is the second, narrower gate: only ops in
            // PROPAGATED_OPERATIONS inject headers so prism opens a SERVER
            // span as a child of this CLIENT span. Non-propagated ops still
            // emit a CLIENT span (for component-render / API timing metrics)
            // but don't continue into the backend
            if (PROPAGATED_OPERATIONS.has(traceInfo.operationName)) {
                const carrier: Record<string, string> = {};
                propagation.inject(spanContext, carrier);
                otelDebugLog(
                    '[OTel] Injecting B3 headers for',
                    traceInfo.operationName,
                    url,
                );
                // Merged headers must be a plain object, not a Headers
                // instance: downstream fetch wrappers spread
                // init.headers with { ...init.headers }, which yields an
                // empty obj for a Headers instance and silently drops
                // content-type header (and others as well), causing Apollo Server
                // to reject the request with CSRF error.
                const normalized = new Headers(init?.headers);
                const mergedHeaders: Record<string, string> = {};
                normalized.forEach((value, key) => {
                    mergedHeaders[key] = value;
                });
                Object.entries(carrier).forEach(([k, v]) => {
                    mergedHeaders[k] = v;
                });
                tracedInit = { ...init, headers: mergedHeaders };
            }

            const fetchPromise = otelContext.with(spanContext, () => {
                return originalFetch.call(window, input, tracedInit);
            });

            return fetchPromise.then(
                (response: any) => {
                    // Span finalisation must never reject the caller's
                    // promise — the network request already succeeded.
                    try {
                        span.setAttribute('http.status_code', response.status);
                        span.setStatus({
                            code: response.ok
                                ? SpanStatusCode.OK
                                : SpanStatusCode.ERROR,
                        });
                        span.end();
                    } catch {
                        /* swallow — never lose the response */
                    }
                    return response;
                },
                (error: any) => {
                    try {
                        span.recordException(error);
                        span.setStatus({
                            code: SpanStatusCode.ERROR,
                            message: error.message,
                        });
                        span.end();
                    } catch {
                        /* swallow — preserve the original error */
                    }
                    throw error;
                },
            );
        } catch {
            // Span creation / header injection threw. End the span if we
            // managed to create one, then fire the request untraced.
            try {
                span?.end();
            } catch {
                /* swallow */
            }
            return originalFetch.call(window, input, init);
        }
    };
};

let observabilityInitialized = false;

export const initializeObservability = async () => {
    if (observabilityInitialized) {
        otelDebugLog('[OTel] Already initialized, skipping duplicate call.');
        return;
    }
    observabilityInitialized = true;

    const observabilityConfig: OBSERVABILITY_CONFIG = isFeatureEnabled(
        FEATURE_FLAG_NAMES.OBSERVABILITY_CONFIG,
    );

    if (!observabilityConfig) {
        otelDebugLog('[OTel] No observabilityConfig flag — tracing disabled.');
        return;
    }

    const sampleRate = observabilityConfig?.telemetrySampleRate ?? 0;
    const forceTraceSampling = flags.getValue(
        FLAGS.forceTraceSampling?.name,
    );

    // Per-session sampling:
    //   - Math.random() returns a value in [0, 1), so
    //     Math.random() < sampleRate is true for a sampleRate
    //     fraction of sessions on average. Example: sampleRate of
    //     0.1 traces ~10% of sessions. The check runs once per
    //     page load, so a session is either traced or not for its
    //     entire lifetime.
    //   - When ?forceTraceSampling=true is set in the URL, the
    //     session is always traced regardless of sampleRate. The
    //     two gates above (isFrontendTracingEnabled and
    //     telemetryEnabled) still apply — this flag cannot enable
    //     tracing on a tenant where it has been turned off.
    const shouldInitialize =
        isFrontendTracingEnabled() &&
        observabilityConfig?.telemetryEnabled &&
        (forceTraceSampling || Math.random() < sampleRate);

    otelDebugLog(
        '[OTel] shouldInitialize:',
        shouldInitialize,
        '| telemetryEnabled:',
        observabilityConfig?.telemetryEnabled,
        '| sampleRate:',
        sampleRate,
    );

    const observabilityPromises = [];
    if (shouldInitialize) {
        // Must run BEFORE any component renders so they can create spans.
        setTelemetryEnabled(true);

        initTelemetry(OTLP_CONFIG);
        // Root span must be created here, before any React render. app.tsx
        // must NOT call startRootSpan — a second call would end this span
        // and create a new trace ID, orphaning early spans.
        startRootSpan('page_load', {
            'page.path': window.location.pathname,
        });
        otelDebugLog(
            '[OTel] TracerProvider initialized. Exporting to:',
            OTLP_CONFIG.traces.url,
            '| service:',
            OTLP_CONFIG.serviceName,
        );
        LOGGER = getLogger('observability-service');

        // Defer via microtask so TracerProvider is fully ready first.
        Promise.resolve()
            .then((): null => {
                installCustomFetchInstrumentation();
                otelDebugLog('[OTel] Custom fetch instrumentation installed.');
                return null;
            })
            .catch((_err: unknown): void => {
                LOGGER.error(
                    '[OTel] Failed to install fetch instrumentation:',
                    _err,
                );
            });
    }
    if (observabilityConfig?.replayEnabled) {
        observabilityPromises.push(
            initializeReplay(
                observabilityConfig?.replaySampleRate ?? 0,
                isUnmaskedReplay(),
            ),
        );
    }
    await Promise.all(observabilityPromises);
};

export * from '@thoughtspot/observability';
export { isTelemetryInitialized } from './trace-context-manager';
